/* ==========================================================================
   OrangeWear — header.js (loaded with defer by sections/header.liquid)
   <site-header>  sticky modes (none | always | scroll-up) + --header-height on :root
   <header-menu>  desktop mega menu disclosures: hover intent, click, keyboard
   Plus: menu drawer housekeeping and theme editor helpers.
   Section load/unload in the theme editor re-runs the custom element
   lifecycle (connectedCallback / disconnectedCallback), which re-initialises
   and cleans up everything below.
   ========================================================================== */
(() => {
  if (customElements.get('site-header')) return;

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const finePointerQuery = window.matchMedia('(pointer: fine)');
  const HOVER_OPEN_DELAY = 120;
  const HOVER_CLOSE_DELAY = 200;
  const SCROLL_TOLERANCE = 8;

  /* ---------- <site-header> ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'none';
      this.section = this.closest('.shopify-section') || this;
      this.lastScrollY = Math.max(window.scrollY, 0);
      this.frame = null;

      this.resizeObserver = new ResizeObserver(() => this.updateHeight());
      this.resizeObserver.observe(this);
      this.updateHeight();

      if (this.mode === 'scroll-up') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.addEventListener('focusin', this.reveal);
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('focusin', this.reveal);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.reveal();
      document.documentElement.style.removeProperty('--header-height');
    }

    /* Sticky offset for other components: 0 when the header doesn't stick. */
    updateHeight() {
      const height = this.mode === 'none' ? 0 : this.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }

    onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateVisibility();
      });
    };

    /* Where the header ends in the normal page flow (the next rendered sibling's top edge). */
    get flowBottom() {
      let next = this.section.nextElementSibling;
      while (next && next.getClientRects().length === 0) next = next.nextElementSibling;
      if (!next) return this.offsetHeight;
      return next.getBoundingClientRect().top + window.scrollY;
    }

    updateVisibility() {
      const scrollY = Math.max(window.scrollY, 0);
      // Keep the header visible while a mega menu, the menu drawer, search or cart is open.
      const expanded = this.querySelector('[aria-expanded="true"]');
      if (expanded || scrollY <= this.flowBottom) {
        this.lastScrollY = scrollY;
        this.reveal();
        return;
      }

      const delta = scrollY - this.lastScrollY;
      if (Math.abs(delta) < SCROLL_TOLERANCE) return;
      this.lastScrollY = scrollY;
      if (delta > 0) {
        this.section.classList.add('is-hidden');
      } else {
        this.reveal();
      }
    }

    reveal = () => {
      this.section.classList.remove('is-hidden');
    };
  }

  /* ---------- <header-menu> ---------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-menu-item]'));
      this.openItem = null;
      this.openSource = null;
      this.openTimer = null;
      this.closeTimer = null;

      this.items.forEach((item) => {
        item.addEventListener('pointerenter', this.onPointerEnter);
        item.addEventListener('pointerleave', this.onPointerLeave);
      });
      this.addEventListener('click', this.onToggleClick);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('dialog:open', this.onDialogOpen);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      desktopQuery.addEventListener('change', this.onBreakpointChange);
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      this.items.forEach((item) => {
        item.removeEventListener('pointerenter', this.onPointerEnter);
        item.removeEventListener('pointerleave', this.onPointerLeave);
      });
      this.removeEventListener('click', this.onToggleClick);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('dialog:open', this.onDialogOpen);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      desktopQuery.removeEventListener('change', this.onBreakpointChange);
      window.removeEventListener('pageshow', this.onPageShow);
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
    }

    open(item, source) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (!item) return;
      if (this.openItem && this.openItem !== item) this.setExpanded(this.openItem, false);
      this.openItem = item;
      this.openSource = source;
      this.setExpanded(item, true);
    }

    close(returnFocus = false) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      const item = this.openItem;
      if (!item) return;
      this.openItem = null;
      this.openSource = null;
      this.setExpanded(item, false);
      if (returnFocus) {
        const toggle = item.querySelector('[data-menu-toggle]');
        if (toggle) toggle.focus();
      }
    }

    setExpanded(item, expanded) {
      const toggle = item.querySelector('[data-menu-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(expanded));
    }

    /* Hover intent (mouse with a fine pointer only). */
    onPointerEnter = (event) => {
      if (event.pointerType !== 'mouse' || !finePointerQuery.matches) return;
      const item = event.currentTarget;
      if (item === this.openItem) {
        clearTimeout(this.closeTimer);
        return;
      }
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => this.open(item, 'hover'), HOVER_OPEN_DELAY);
    };

    onPointerLeave = (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.openTimer);
      if (event.currentTarget !== this.openItem || this.openSource !== 'hover') return;
      this.closeTimer = setTimeout(() => this.close(), HOVER_CLOSE_DELAY);
    };

    /* Click, Enter or Space on a toggle button. */
    onToggleClick = (event) => {
      const toggle = event.target.closest('[data-menu-toggle]');
      const item = toggle && toggle.closest('[data-menu-item]');
      if (!item) return;
      if (item !== this.openItem) {
        this.open(item, 'click');
      } else if (this.openSource === 'hover' && event.detail > 0) {
        // A mouse click on a panel that hover intent just opened keeps it open.
        this.openSource = 'click';
        clearTimeout(this.closeTimer);
      } else {
        this.close();
      }
    };

    onKeydown = (event) => {
      if (event.key !== 'Escape' || !this.openItem) return;
      this.close(this.openItem.contains(document.activeElement));
    };

    onFocusOut = (event) => {
      if (!this.openItem || !this.openItem.contains(event.target)) return;
      // relatedTarget is null when focus goes nowhere (e.g. a click on the panel background);
      // outside clicks are handled by onDocumentClick.
      if (event.relatedTarget && !this.openItem.contains(event.relatedTarget)) this.close();
    };

    onDocumentClick = (event) => {
      if (this.openItem && !this.openItem.contains(event.target)) this.close();
    };

    onDialogOpen = () => this.close();

    onBreakpointChange = () => this.close();

    onPageShow = (event) => {
      if (event.persisted) this.close();
    };

    /* Theme editor: show the panel that contains the selected promotion block. */
    onBlockSelect = (event) => {
      const item = event.target instanceof Element ? event.target.closest('[data-menu-item]') : null;
      if (item && this.contains(item)) this.open(item, 'editor');
    };

    onBlockDeselect = (event) => {
      if (this.openItem && event.target instanceof Node && this.openItem.contains(event.target)) this.close();
    };
  }

  customElements.define('site-header', SiteHeader);
  if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);

  /* ---------- Menu drawer ---------- */
  const closeMenuDrawer = (immediate = false) => {
    const drawer = document.getElementById('MenuDrawer');
    if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;
    if (immediate || !window.theme || typeof window.theme.closeDialog !== 'function') {
      drawer.close();
    } else {
      window.theme.closeDialog(drawer);
    }
  };

  // The drawer is for smaller screens: close it when the viewport grows to desktop.
  desktopQuery.addEventListener('change', (event) => {
    if (event.matches) closeMenuDrawer();
  });

  // Restored from the back/forward cache with the drawer still open.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) closeMenuDrawer(true);
  });

  // Links to an anchor on the current page don't navigate away, so close the drawer.
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('#MenuDrawer a[href]') : null;
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.hash && url.pathname === window.location.pathname) closeMenuDrawer();
  });

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:section:select', (event) => {
    const header = event.target instanceof Element ? event.target.querySelector('site-header') : null;
    if (header) header.reveal();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const drawer = event.target instanceof Element ? event.target.querySelector('#MenuDrawer') : null;
    if (drawer instanceof HTMLDialogElement && drawer.open) drawer.close();
  });

  // Announcement bar on small screens: scroll to the selected announcement.
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target instanceof Element ? event.target.closest('.announcement-bar__item') : null;
    const slider = slide && slide.closest('slider-component');
    if (!slider || typeof slider.scrollToIndex !== 'function') return;
    slider.scrollToIndex(Array.from(slide.parentElement.children).indexOf(slide));
  });
})();
